/**
 * ClinicListPage — entry-point list of clinics with morning-glance cards.
 *
 * V1.1 rewrite (V1.1 plan §5.7): each clinic card now shows the next
 * upcoming review + last 4 past reviews via `useClinicMeetings`. Each
 * past review is a deep link into the pinned-meeting dashboard.
 *
 * Per V1.1 plan §5.7 N+1 stance: one `useClinicMeetings` query per
 * clinic. Acceptable at dev-seed scale (1-3 clinics); V1.2 may add a
 * batch endpoint when real scale warrants.
 */
import { Link } from "react-router-dom";
import { Spinner } from "@tensaw/design-system";
import { useClinics } from "@/hooks/useClinics";
import { ClinicCard } from "./clinics/ClinicCard";

export function ClinicListPage() {
  const clinicsQ = useClinics();

  if (clinicsQ.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center" role="status" aria-label="Loading">
        <Spinner />
      </div>
    );
  }

  if (clinicsQ.isError || !clinicsQ.data) {
    return (
      <div className="rounded-lg border border-border bg-white p-8">
        <h2 className="text-lg font-semibold text-gray-800">Couldn&apos;t load clinics</h2>
        <p className="mt-2 text-sm text-gray-600">
          {clinicsQ.error instanceof Error
            ? clinicsQ.error.message
            : "An unknown error occurred."}
        </p>
        <button
          type="button"
          onClick={() => void clinicsQ.refetch()}
          className="mt-4 inline-flex items-center rounded-button bg-brand-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-hover"
        >
          Retry
        </button>
      </div>
    );
  }

  const clinics = clinicsQ.data.clinics;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-tw-teal-400">
          MIPS Dashboard · V1.1
        </div>
        <h1 className="text-2xl font-semibold text-gray-800">
          Your clinics ({clinics.length})
        </h1>
        <p className="mt-1 text-sm text-gray-600">
          Pick a clinic to open its Quarterly Review dashboard, or jump
          directly into a past review.
        </p>
      </div>

      {clinics.length === 0 ? (
        <div className="rounded-lg border border-border bg-white p-8 text-center">
          <p className="text-gray-600">
            No MIPS-active clinics in this tenant. Activate one in the admin
            page first.
          </p>
          <Link
            to="/admin/clinics"
            className="mt-3 inline-block text-sm font-semibold text-tw-teal-700 hover:underline"
          >
            Go to admin · clinics →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {clinics.map((c) => (
            <ClinicCard key={c.clinic_id} clinic={c} />
          ))}
        </div>
      )}
    </div>
  );
}
